import * as React from 'react';
import classNames from 'classnames/bind';
import { default as ReactSelect, OptionTypeBase, Styles } from 'react-select';

import styles from './select.module.css';
import { SingleValue } from './SingleValue';
import { SelectComponents } from 'react-select/src/components';
import { GetControlLabel } from './types';

const cx = classNames.bind(styles);

type Props = {
  /** If the menu options are wider than the control itself, use this to set a
   * larger minimum menu width */
  minMenuWidth?: string;

  /** Takes a function that receives the selected option as a parameter and
   * returns a string/JSX that's displayed on the control  */
  getControlLabel?: GetControlLabel;

  /** Pass this value *always* to make sure the right color scheme is used  */
  appTheme?: 'dark' | 'light';
} & React.ComponentProps<typeof ReactSelect>;

const LIGHT_COLORS = {
  neutral20: 'hsl(0, 0%, 74%)',
  primary: 'hsl(0, 0%, 94%)', // Selected option
  primary25: 'hsl(0, 0%, 97%)', // Hover option
  primary50: 'hsl(0, 0, 96%)',
};

const DARK_COLORS = {
  neutral0: 'hsl(223, 7%, 20%)',
  neutral20: 'hsl(0, 0%, 40%)',
  neutral80: 'hsl(0, 0%, 67%)',
  neutral90: 'hsl(0, 0%, 100%)',
  primary: 'hsl(220, 8%, 23%)', // Selected option
  primary25: 'hsl(220, 8%, 14%)', // Hover option
  primary50: 'hsl(220, 8%, 14%)', // Active non-selected element
};

export const Select: React.FC<Props> = props => {
  const {
    options,
    className,
    styles: userStyles,
    minMenuWidth,
    getControlLabel,
    appTheme = 'light',
    ...restProps
  } = props;

  const styles: Partial<Styles<OptionTypeBase, boolean>> = {
    control: provided => ({
      ...provided,
      minHeight: '32px',
    }),
    menu: provided => ({
      ...provided,
      marginTop: '0',
      marginBottom: '0',
      ...(minMenuWidth ? { minWidth: minMenuWidth } : null),
    }),
    dropdownIndicator: provided => ({
      ...provided,
      padding: '4px',
    }),
    indicatorSeparator: provided => ({
      ...provided,
      display: 'none',
    }),
    singleValue: provided => ({
      ...provided,
      overflow: 'visible',
    }),
    ...userStyles,
  };

  const componentOverrides = {} as Partial<SelectComponents<any, boolean>>;

  if (typeof getControlLabel !== 'undefined') {
    componentOverrides['SingleValue'] = SingleValue;
  }

  return (
    <ReactSelect
      className={cx('select', className)}
      classNamePrefix={'f-react-select'}
      components={componentOverrides}
      styles={styles}
      theme={current => ({
        ...current,
        colors: {
          ...current.colors,
          ...(appTheme === 'dark' ? DARK_COLORS : LIGHT_COLORS),
        },
      })}
      options={options}
      getControlLabel={getControlLabel}
      {...restProps}
    />
  );
};
